import React, { useRef, useState } from 'react';

import { LayoutGroup } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { EmojiPickerCombo, type EmojiPickerComboProps, Sheet } from '@pkg/components';
import { getPagesContent, useFollowersPanel, useGetGroupFollowers } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { MembersList } from '../sections/MembersList';
import { renderMemberCounts } from '../sections/MembersList/utilities';

import { Search } from './components';

export interface FollowersPanelProps {
    onClose(): void;
}

export function FollowersPanel({ onClose }: FollowersPanelProps) {
    const [translate] = useTranslation('groups');
    const { desktop } = useMediaQuery();

    const groupId = useFollowersPanel().groupId || '';

    const [searchQuery, setSearchQuery] = useState('');

    const searchRef = useRef<HTMLDivElement>(null);
    const {
        data: followersRawData,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetching,
    } = useGetGroupFollowers({ groupId });
    const followers = getPagesContent(followersRawData?.pages);

    const handleEmojiSelect: EmojiPickerComboProps['onSelectEmoji'] = (emoji) =>
        setSearchQuery((value) => `${value}${emoji}`);

    function handleQueryChange(query: string) {
        setSearchQuery(query);
    }

    function handleClearQuery() {
        setSearchQuery('');
    }

    const emojiPickerMarkup = desktop ? (
        <EmojiPickerCombo closeExclusions={[searchRef.current]} onSelectEmoji={handleEmojiSelect} />
    ) : null;

    const resultHeader = renderMemberCounts({
        followers: followers,
        translateFn: translate,
    });

    return (
        <Sheet backdrop onClose={onClose}>
            <Sheet.Header title={translate('pods_followers_panel_title')} onClose={onClose} />

            {/* TODO: Implement SkeletonLoader for when `groupId` is falsey */}
            <Sheet.Main>
                <Sheet.Layout>
                    <Sheet.Layout.Columns stretch>
                        <Sheet.Layout.Columns.Primary padded scrollable>
                            <LayoutGroup>
                                <Search
                                    ref={searchRef}
                                    groupId={groupId}
                                    emojiPicker={emojiPickerMarkup}
                                    query={searchQuery}
                                    onQueryChange={handleQueryChange}
                                    onSelect={handleClearQuery}
                                />

                                <MembersList
                                    listHeader={resultHeader}
                                    fetchNextPage={fetchNextPage}
                                    hasNextPage={!!hasNextPage}
                                    isFetching={isFetching}
                                    isLoading={isLoading}
                                    members={followers}
                                />
                            </LayoutGroup>
                        </Sheet.Layout.Columns.Primary>
                    </Sheet.Layout.Columns>
                </Sheet.Layout>
            </Sheet.Main>
        </Sheet>
    );
}
